/**
 * src/components/notes/NoteThread.jsx — one root note + its reply thread.
 *
 * Renders a root NoteCard and, indented beneath it, that root's replies
 * (oldest -> newest) behind a subtle var(--bdrg) thread rail. Any authenticated
 * user can Reply (opens a small composer -> createNote with parentId=root.id);
 * the AUTHOR of any entry can Edit it; the AUTHOR of the root can re-tag it.
 *
 * Mobile-first, 44px touch targets, ACN design tokens only. New data-testids:
 *   notes-reply-<rootId>, notes-reply-input-<rootId>, notes-reply-send-<rootId>,
 *   note-thread-<rootId> (the thread wrapper).
 */

import { useState } from 'react';
import { MessageSquarePlus, Send } from 'lucide-react';
import NoteCard from './NoteCard';

export default function NoteThread({
  root,
  replies = [],
  canArchive,
  canEdit,
  onArchive,
  onEdit,
  onChangeTag,
  onReply,
  t,
}) {
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyBody, setReplyBody] = useState('');
  const [sending, setSending] = useState(false);

  const sendReply = async () => {
    const trimmed = replyBody.trim();
    if (trimmed.length === 0 || sending) return;
    setSending(true);
    try {
      await onReply?.(root.id, trimmed);
      setReplyBody('');
      setReplyOpen(false);
    } catch {
      // TICKET-CODEX-3371361574: onReply (handleReply in NotesTab) re-throws
      // on { success: false } so execution never reaches the clear/close lines
      // above — the textarea stays open with the user's text intact. The error
      // message is already surfaced via NotesTab's setError; swallow here to
      // prevent an unhandled-rejection warning.
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid={`note-thread-${root.id}`} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <NoteCard
        note={root}
        canArchive={canArchive}
        onArchive={onArchive}
        canEdit={canEdit(root)}
        onEdit={onEdit}
        onChangeTag={onChangeTag}
        allowTagChange
        t={t}
      />

      {/* Indented reply column behind a thread rail. */}
      {replies.length > 0 && (
        <div style={{
          display: 'flex', flexDirection: 'column', gap: 8,
          paddingLeft: 16, marginLeft: 6, borderLeft: '2px solid var(--bdrg)',
        }}>
          {replies.map((r) => (
            <NoteCard
              key={r.id}
              note={r}
              canArchive={canArchive}
              onArchive={onArchive}
              canEdit={canEdit(r)}
              onEdit={onEdit}
              reply
              t={t}
            />
          ))}
        </div>
      )}

      {/* Reply affordance — any authenticated user. */}
      <div style={{ paddingLeft: 16, marginLeft: 6 }}>
        {replyOpen ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <textarea
              data-testid={`notes-reply-input-${root.id}`}
              value={replyBody}
              onChange={(e) => setReplyBody(e.target.value)}
              placeholder={t('notes.replyPlaceholder')}
              rows={2}
              style={{
                width: '100%', padding: 10, borderRadius: 8,
                border: '1px solid var(--bdr)', background: 'var(--card)', color: 'var(--t1)',
                fontSize: 14, fontFamily: 'inherit', resize: 'vertical',
                outline: 'none', minHeight: 56, lineHeight: 1.5,
              }}
            />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                data-testid={`notes-reply-cancel-${root.id}`}
                onClick={() => { setReplyOpen(false); setReplyBody(''); }}
                style={{
                  display: 'inline-flex', alignItems: 'center',
                  minHeight: 44, padding: '8px 14px', borderRadius: 10,
                  fontSize: 13, fontWeight: 600,
                  background: 'var(--glass)', color: 'var(--tm)',
                  border: '1px solid var(--bdr)', cursor: 'pointer',
                }}
              >
                {t('notes.cancel')}
              </button>
              <button
                data-testid={`notes-reply-send-${root.id}`}
                onClick={sendReply}
                disabled={sending || replyBody.trim().length === 0}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  minHeight: 44, padding: '8px 16px', borderRadius: 10,
                  fontSize: 13, fontWeight: 600,
                  background: 'var(--navy-surface)', color: 'var(--on-navy)', border: 'none',
                  cursor: sending || replyBody.trim().length === 0 ? 'default' : 'pointer',
                  opacity: sending || replyBody.trim().length === 0 ? 0.5 : 1,
                }}
              >
                <Send size={14} />
                {sending ? t('notes.saving') : t('notes.reply')}
              </button>
            </div>
          </div>
        ) : (
          <button
            data-testid={`notes-reply-${root.id}`}
            onClick={() => setReplyOpen(true)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              minHeight: 44, padding: '8px 12px', borderRadius: 99,
              fontSize: 12, fontWeight: 600,
              background: 'transparent', color: 'var(--tm)',
              border: 'none', cursor: 'pointer',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--accent)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--tm)'; }}
          >
            <MessageSquarePlus size={14} />
            {t('notes.reply')}
          </button>
        )}
      </div>
    </div>
  );
}
